import React from 'react';
import styled from 'styled-components';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning' | 'danger' | 'success';
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(2px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: modalSlideIn 0.2s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const Header = styled.div<{ $type: string }>`
  padding: 1.5rem;
  text-align: center;
  background: ${props => {
    switch (props.$type) {
      case 'danger': return 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
      case 'warning': return 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
      case 'success': return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
      default: return 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
    }
  }};
`;

const Icon = styled.div<{ $type: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: ${props => {
    switch (props.$type) {
      case 'danger': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#3b82f6';
    }
  }};
  color: white;
`;

const Title = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const Message = styled.p`
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  padding: 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  background: #f9fafb;
`;

const Button = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 80px;

  ${props => props.$variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const DangerButton = styled(Button)`
  background: #ef4444;
  color: white;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }
`;

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'info'
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'danger': return '⚠️';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      default: return 'ℹ️';
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Header $type={type}>
          <Icon $type={type}>{getIcon()}</Icon>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Header>
        <ButtonContainer>
          <Button $variant="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          {type === 'danger' ? (
            <DangerButton $variant="primary" onClick={handleConfirm}>
              {confirmText}
            </DangerButton>
          ) : (
            <Button $variant="primary" onClick={handleConfirm}>
              {confirmText}
            </Button>
          )}
        </ButtonContainer>
      </ModalContainer>
    </Overlay>
  );
};

export default ConfirmModal; 
